/**
 * AddEmailForm Component (AC-921)
 *
 * Unified textarea for adding single or multiple emails.
 * Accepts comma-separated or newline-separated emails.
 * UX Review: No single/bulk toggle - unified textarea approach.
 *
 * Props:
 *   onAddSingle: (email: string) => Promise<void>
 *   onAddBulk: (emails: string[]) => Promise<void>
 *   isLoading: boolean
 *
 * Epic: AC-915 - Soft-Launch Email Allowlist with Admin Portal
 */

import React, { useState } from 'react';
import { Plus } from 'lucide-react';

interface AddEmailFormProps {
  onAddSingle: (email: string) => Promise<void>;
  onAddBulk: (emails: string[]) => Promise<void>;
  isLoading: boolean;
}

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const AddEmailForm: React.FC<AddEmailFormProps> = ({
  onAddSingle,
  onAddBulk,
  isLoading,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const parseEmails = (input: string): string[] => {
    // Split by commas and newlines, trim whitespace, filter empty strings
    return input
      .split(/[,\n]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = inputValue.trim();
    if (!trimmed) {
      return;
    }

    const emails = parseEmails(trimmed);

    if (emails.length === 0) {
      return;
    }

    // Validate all emails
    const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email));
    if (invalidEmails.length > 0) {
      setError(`Invalid email format: ${invalidEmails.join(', ')}`);
      return;
    }

    try {
      if (emails.length === 1) {
        await onAddSingle(emails[0]);
      } else {
        await onAddBulk(emails);
      }
      setInputValue('');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to add email(s)');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <textarea
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (error) setError(null);
          }}
          disabled={isLoading}
          placeholder="Enter email addresses (comma or newline separated)"
          className="input w-full h-24 resize-y"
          rows={3}
        />
      </div>

      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="btn btn-primary bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Add Emails
      </button>
    </form>
  );
};

export default AddEmailForm;
